import {
  createUnifiedTheme,
  genPageTheme,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';
import { fluxColors, fluxFontFamily, fluxGradients } from './fluxTokens';

const lightPageThemes = {
  home: genPageTheme({
    colors: ['#9f79b4', '#6e3a8f'],
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: ['#9177e3', '#6e3a8f'],
    shape: shapes.wave,
  }),
  tool: genPageTheme({
    colors: ['#7b62de', '#552569'],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: ['#9f79b4', '#552569'],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: ['#74caff', '#0c53b7'],
    shape: shapes.wave,
  }),
  library: genPageTheme({
    colors: ['#54d62c', '#229a16'],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: ['#9f79b4', '#6e3a8f'],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: ['#9f79b4', '#6e3a8f'],
    shape: shapes.wave,
  }),
  apis: genPageTheme({
    colors: ['#74caff', '#1890ff'],
    shape: shapes.wave,
  }),
  /** Used by ItemCardHeader (Create, Applications, etc.) */
  card: genPageTheme({
    colors: ['#9f79b4', '#6e3a8f'],
    shape: shapes.wave,
  }),
};

/** Nisaba dark canvas for page chrome; cards keep Flux purple for contrast */
const darkPageThemes = {
  home: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  tool: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  library: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  apis: genPageTheme({
    colors: ['#161c24', '#171f27'],
    shape: shapes.wave,
  }),
  card: genPageTheme({
    colors: ['#9f79b4', '#3d174f'],
    shape: shapes.wave,
  }),
};

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: fluxFontFamily,
        backgroundColor: 'var(--surface-background, #161c24)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        borderRadius: 6,
        fontWeight: 500,
        fontFamily: fluxFontFamily,
        minHeight: 36,
        transition: 'all 0.2s ease',
      },
      containedPrimary: {
        background: fluxGradients.primary,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          background: fluxGradients.primary,
          filter: 'brightness(1.05)',
        },
      },
      outlined: {
        borderRadius: 6,
      },
      sizeSmall: {
        minHeight: 32,
        padding: '0 10px',
        fontSize: '0.8125rem',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 8,
      },
      outlined: {
        borderColor: 'var(--border-color, rgba(112, 123, 135, 0.24))',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        border: '1px solid var(--color-divider, rgba(112, 123, 135, 0.24))',
        borderRadius: 8,
        boxShadow: 'var(--shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05))',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--color-divider, rgba(112, 123, 135, 0.24))',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--border-color, #dfe3e8)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-grey-400, #c4cdd5)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--interactive-primary, #6e3a8f)',
          borderWidth: 1,
        },
      },
      input: {
        paddingTop: 10,
        paddingBottom: 10,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontFamily: fluxFontFamily,
        borderBottom: '1px solid var(--color-divider, rgba(112, 123, 135, 0.24))',
      },
      head: {
        fontWeight: 600,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontFamily: fluxFontFamily,
      },
      outlined: {
        borderColor: 'var(--border-color, #dfe3e8)',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 500,
        fontFamily: fluxFontFamily,
        minHeight: 40,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundImage: fluxGradients.primary,
        boxShadow: 'none',
        borderBottom: '1px solid var(--color-divider, transparent)',
        padding: '10px 24px',
        minHeight: 0,
      },
      leftItemsBox: {
        paddingTop: 0,
        paddingBottom: 0,
      },
      title: {
        fontFamily: fluxFontFamily,
        fontWeight: 700,
        fontSize: '1.35rem',
        lineHeight: 1.25,
        marginBottom: 0,
      },
      subtitle: {
        fontFamily: fluxFontFamily,
        opacity: 0.9,
        marginTop: 2,
        fontSize: '0.8125rem',
        lineHeight: 1.35,
      },
      type: {
        marginBottom: 2,
        fontSize: 10,
      },
    },
  },
  BackstageHeaderLabel: {
    styleOverrides: {
      root: {
        fontFamily: fluxFontFamily,
      },
    },
  },
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        fontFamily: fluxFontFamily,
        borderRight: '1px solid var(--color-divider, rgba(112, 123, 135, 0.24))',
        boxShadow: 'none',
      },
    },
  },
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        fontFamily: fluxFontFamily,
        borderRadius: 6,
        margin: '2px 8px',
        width: 'auto',
        border: '1px solid transparent',
        transition: 'all 0.2s ease-in-out',
      },
      selected: {
        border: '1px solid var(--color-primary-transparent-32, rgba(110, 58, 143, 0.32))',
        backgroundColor:
          'var(--color-primary-transparent-16, rgba(110, 58, 143, 0.16))',
        boxShadow: '0 1px 4px var(--color-primary-transparent-16, rgba(110, 58, 143, 0.16))',
        // Neutraliza la barra lateral izquierda por defecto de Backstage
        borderLeft:
          '1px solid var(--color-primary-transparent-32, rgba(110, 58, 143, 0.32))',
      },
    },
  },
  BackstageSidebarDivider: {
    styleOverrides: {
      root: {
        height: 1,
        width: 'calc(100% - 24px)',
        margin: '8px 12px',
        padding: 0,
        border: 'none',
        background: 'var(--color-divider, rgba(112, 123, 135, 0.24))',
      },
    },
  },
  BackstagePage: {
    styleOverrides: {
      root: {
        fontFamily: fluxFontFamily,
      },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        color: '#ffffff',
      },
    },
  },
};

export const fluxLightTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    type: 'light',
    mode: 'light',
    background: {
      default: fluxColors.white,
      paper: fluxColors.white,
    },
    primary: {
      light: fluxColors.primary.light,
      main: fluxColors.primary.main,
      dark: fluxColors.primary.dark,
      contrastText: fluxColors.white,
    },
    secondary: {
      light: fluxColors.secondary.light,
      main: fluxColors.secondary.main,
      dark: fluxColors.secondary.dark,
      contrastText: fluxColors.white,
    },
    error: {
      light: fluxColors.error.light,
      main: fluxColors.error.main,
      dark: fluxColors.error.dark,
    },
    warning: {
      light: fluxColors.warning.light,
      main: fluxColors.warning.main,
      dark: fluxColors.warning.dark,
    },
    info: {
      light: fluxColors.info.light,
      main: fluxColors.info.main,
      dark: fluxColors.info.dark,
    },
    success: {
      light: fluxColors.success.light,
      main: fluxColors.success.main,
      dark: fluxColors.success.dark,
    },
    text: {
      primary: fluxColors.grey[800],
      secondary: fluxColors.grey[600],
      disabled: fluxColors.grey[500],
    },
    divider: 'rgba(112, 123, 135, 0.24)',
    action: {
      active: fluxColors.grey[600],
      hover: 'rgba(145, 158, 171, 0.08)',
      selected: 'rgba(145, 158, 171, 0.16)',
      disabled: 'rgba(145, 158, 171, 0.8)',
      disabledBackground: 'rgba(159, 173, 187, 0.24)',
    },
    grey: {
      50: fluxColors.grey[100],
      100: fluxColors.grey[100],
      200: fluxColors.grey[200],
      300: fluxColors.grey[300],
      400: fluxColors.grey[400],
      500: fluxColors.grey[500],
      600: fluxColors.grey[600],
      700: fluxColors.grey[700],
      800: fluxColors.grey[800],
      900: fluxColors.grey[900],
    },
    status: {
      ok: fluxColors.success.main,
      warning: fluxColors.warning.main,
      error: fluxColors.error.main,
      running: fluxColors.info.main,
      pending: fluxColors.warning.light,
      aborted: fluxColors.grey[500],
    },
    bursts: {
      fontColor: fluxColors.white,
      slackChannelText: '#ddd',
      backgroundColor: { default: fluxColors.primary.main },
      gradient: { linear: fluxGradients.primary },
    },
    banner: {
      info: fluxColors.info.main,
      error: fluxColors.error.main,
      text: fluxColors.white,
      link: fluxColors.white,
      closeButtonColor: fluxColors.white,
      warning: fluxColors.warning.main,
    },
    border: fluxColors.grey[300],
    textContrast: fluxColors.grey[800],
    textVerySubtle: fluxColors.grey[300],
    textSubtle: fluxColors.grey[600],
    highlight: fluxColors.primary.lighter,
    errorBackground: fluxColors.error.lighter,
    warningBackground: fluxColors.warning.lighter,
    infoBackground: fluxColors.info.lighter,
    errorText: fluxColors.error.dark,
    infoText: fluxColors.info.dark,
    warningText: fluxColors.warning.dark,
    linkHover: fluxColors.primary.dark,
    link: fluxColors.primary.main,
    gold: fluxColors.warning.main,
    navigation: {
      background: fluxColors.white,
      indicator: fluxColors.primary.main,
      color: fluxColors.grey[600],
      selectedColor: fluxColors.primary.main,
      navItem: {
        hoverBackground: 'rgba(145, 158, 171, 0.08)',
      },
      submenu: {
        background: fluxColors.grey[100],
      },
    },
    pinSidebarButton: {
      icon: fluxColors.grey[600],
      background: fluxColors.grey[200],
    },
    tabbar: {
      indicator: fluxColors.primary.main,
    },
  } as any,
  fontFamily: fluxFontFamily,
  defaultPageTheme: 'home',
  pageTheme: lightPageThemes,
  components: sharedComponents as any,
});

export const fluxDarkTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    type: 'dark',
    mode: 'dark',
    background: {
      default: fluxColors.grey[900],
      paper: fluxColors.paperDark,
    },
    primary: {
      light: fluxColors.primary.lighter,
      main: fluxColors.primary.light,
      dark: fluxColors.primary.main,
      contrastText: fluxColors.white,
    },
    secondary: {
      light: fluxColors.secondary.lighter,
      main: fluxColors.secondary.light,
      dark: fluxColors.secondary.main,
      contrastText: fluxColors.white,
    },
    error: {
      light: fluxColors.error.light,
      main: fluxColors.error.main,
      dark: fluxColors.error.dark,
    },
    warning: {
      light: fluxColors.warning.light,
      main: fluxColors.warning.main,
      dark: fluxColors.warning.dark,
    },
    info: {
      light: fluxColors.info.light,
      main: fluxColors.info.main,
      dark: fluxColors.info.dark,
    },
    success: {
      light: fluxColors.success.light,
      main: fluxColors.success.main,
      dark: fluxColors.success.dark,
    },
    text: {
      primary: fluxColors.white,
      secondary: fluxColors.grey[500],
      disabled: fluxColors.grey[600],
    },
    divider: 'rgba(226, 226, 226, 0.24)',
    action: {
      active: fluxColors.grey[500],
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(145, 158, 171, 0.8)',
      disabledBackground: 'rgba(145, 158, 171, 0.24)',
    },
    grey: {
      50: fluxColors.grey[100],
      100: fluxColors.grey[100],
      200: fluxColors.grey[200],
      300: fluxColors.grey[300],
      400: fluxColors.grey[400],
      500: fluxColors.grey[500],
      600: fluxColors.grey[600],
      700: fluxColors.grey[700],
      800: fluxColors.grey[800],
      900: fluxColors.grey[900],
    },
    status: {
      ok: fluxColors.success.light,
      warning: fluxColors.warning.light,
      error: fluxColors.error.light,
      running: fluxColors.info.light,
      pending: fluxColors.warning.main,
      aborted: fluxColors.grey[500],
    },
    bursts: {
      fontColor: fluxColors.white,
      slackChannelText: '#ddd',
      backgroundColor: { default: fluxColors.primary.main },
      gradient: { linear: fluxGradients.primary },
    },
    banner: {
      info: fluxColors.info.light,
      error: fluxColors.error.light,
      text: fluxColors.white,
      link: fluxColors.white,
      closeButtonColor: fluxColors.white,
      warning: fluxColors.warning.light,
    },
    border: fluxColors.grey[700],
    textContrast: fluxColors.white,
    textVerySubtle: fluxColors.grey[700],
    textSubtle: fluxColors.grey[500],
    highlight: 'rgba(159, 121, 180, 0.24)',
    errorBackground: 'rgba(255, 72, 66, 0.16)',
    warningBackground: 'rgba(255, 193, 7, 0.16)',
    infoBackground: 'rgba(24, 144, 255, 0.16)',
    errorText: fluxColors.error.light,
    infoText: fluxColors.info.light,
    warningText: fluxColors.warning.light,
    linkHover: fluxColors.primary.lighter,
    link: fluxColors.primary.light,
    gold: fluxColors.warning.light,
    navigation: {
      background: fluxColors.grey[900],
      indicator: fluxColors.primary.light,
      color: fluxColors.grey[500],
      selectedColor: fluxColors.primary.lighter,
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.08)',
      },
      submenu: {
        background: fluxColors.grey[800],
      },
    },
    pinSidebarButton: {
      icon: fluxColors.grey[400],
      background: fluxColors.grey[700],
    },
    tabbar: {
      indicator: fluxColors.primary.light,
    },
  } as any,
  fontFamily: fluxFontFamily,
  defaultPageTheme: 'home',
  pageTheme: darkPageThemes,
  components: sharedComponents as any,
});
